import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../../contexts/ProjectContext';
import { Action } from '../../lib/auth';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { ScrollArea } from '../ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import {
  Building2,
  CalendarDays,
  Lock,
  ChevronDown,
  Check,
  Loader2,
  Search,
} from 'lucide-react';

const ActiveProjectHeader = () => {
  const navigate = useNavigate();
  const { activeProject, activeSchedule, loading, projects, fetchProjects, loadProject, can } = useProject();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [projectsList, setProjectsList] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [switchingProject, setSwitchingProject] = useState(false);

  const isReadOnly = !can(Action.MANAGE_SCHEDULES);

  // Filter projects based on search query
  const filteredProjects = (projects || []).filter((project) =>
    project.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    project.code?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle project switch from inline dropdown
  const handleProjectSwitch = async (projectId) => {
    if (projectId === activeProject?.id) {
      setIsDropdownOpen(false);
      return;
    }

    if (activeProject) {
      // Already in a project — open the new one in a new tab
      setIsDropdownOpen(false);
      window.open(`/projects/${projectId}`, '_blank');
    } else {
      try {
        await loadProject(projectId);
        setIsDropdownOpen(false);
        navigate('/scheduling');
      } catch (error) {
        console.error('Failed to switch project:', error);
      }
    }
  };

  // Fetch projects when dropdown opens
  const loadProjects = useCallback(async () => {
    if (projectsList.length > 0) return; // Already loaded
    setLoadingProjects(true);
    try {
      const data = await fetchProjects();
      setProjectsList(data || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  }, [fetchProjects, projectsList.length]);

  // Load projects when popover or inline dropdown opens
  useEffect(() => {
    if (open || isDropdownOpen) {
      loadProjects();
    }
  }, [open, isDropdownOpen, loadProjects]);

  // Handle project selection
  const handleSelectProject = async (project) => {
    if (project.id === activeProject?.id) {
      setOpen(false);
      return;
    }

    if (activeProject) {
      // Already in a project — open the new one in a new tab
      setOpen(false);
      window.open(`/projects/${project.id}`, '_blank');
    } else {
      setSwitchingProject(true);
      try {
        await loadProject(project.id);
        setOpen(false);
        // Navigate to scheduling view for the selected project
        navigate('/scheduling');
      } catch (error) {
        console.error('Failed to switch project:', error);
      } finally {
        setSwitchingProject(false);
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Active', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      completed: { label: 'Completed', className: 'bg-slate-100 text-slate-500 border-slate-200' },
      on_hold: { label: 'On Hold', className: 'bg-amber-50 text-amber-700 border-amber-200' },
      planning: { label: 'Planning', className: 'bg-blue-50 text-blue-700 border-blue-200' },
    };
    const config = statusConfig[status] || statusConfig.active;
    return (
      <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-4 font-medium ${config.className}`}>
        {config.label}
      </Badge>
    );
  };

  // No project selected state - show selector prompt
  if (!activeProject) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            className="flex items-center gap-2 h-8 px-3 text-slate-500 hover:text-slate-700 hover:bg-slate-100"
            data-testid="no-project-selector"
          >
            <Building2 className="h-4 w-4" />
            <span className="text-sm">Select a project</span>
            <ChevronDown className="h-3 w-3 ml-1" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-0" align="start" data-testid="project-selector-dropdown">
          <Command>
            <CommandInput placeholder="Search projects..." data-testid="project-search-input" />
            <CommandList>
              {loadingProjects ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                  <span className="ml-2 text-sm text-slate-500">Loading projects...</span>
                </div>
              ) : (
                <>
                  <CommandEmpty>No projects found.</CommandEmpty>
                  <CommandGroup heading="Projects">
                    {projectsList.map((project) => (
                      <CommandItem
                        key={project.id}
                        value={`${project.name} ${project.code || ''}`}
                        onSelect={() => handleSelectProject(project)}
                        className="flex items-center gap-2 cursor-pointer"
                        data-testid={`project-option-${project.id}`}
                      >
                        <Building2 className="h-4 w-4 text-slate-400" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium truncate">{project.name}</span>
                            {getStatusBadge(project.status)}
                          </div>
                          {project.code && (
                            <span className="text-[10px] font-mono text-slate-400">{project.code}</span>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }

  // Project selected - show with project switcher dropdown
  return (
    <div className="flex items-center gap-2" data-testid="active-project-header">
      {/* Project Context Display (clickable to switch) */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="flex items-center h-8 px-3 gap-2 text-slate-700 hover:bg-slate-50 rounded-md transition-colors cursor-pointer"
          data-testid="active-project-display"
        >
          <Building2 className="h-4 w-4 text-slate-500" />
          <div className="flex items-center gap-2">
            {/* Project Code */}
            <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
              {activeProject.code || '—'}
            </span>
            {/* Project Name */}
            <span className="text-sm font-medium max-w-[200px] truncate">
              {activeProject.name}
            </span>
            {/* Status Badge */}
            {getStatusBadge(activeProject.status)}
          </div>
          <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
          {isReadOnly && (
            <Lock className="h-3 w-3 text-slate-300" />
          )}
        </button>

        {/* Project Switcher Dropdown */}
        {isDropdownOpen && (
          <div className="absolute top-full left-0 mt-1 w-80 bg-white rounded-lg border border-slate-200 shadow-lg z-50">
            {/* Search */}
            <div className="p-2 border-b border-slate-100">
              <div className="flex items-center gap-2 px-2 py-1.5 bg-slate-50 rounded-md">
                <Search className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400"
                />
              </div>
            </div>

            {/* Project List */}
            <ScrollArea className="max-h-64">
              <div className="p-1">
                {filteredProjects.length === 0 ? (
                  <div className="px-3 py-4 text-center text-sm text-slate-400">
                    {loading ? 'Loading projects...' : 'No projects found'}
                  </div>
                ) : (
                  filteredProjects.map((project) => (
                    <button
                      key={project.id}
                      onClick={() => handleProjectSwitch(project.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors ${
                        project.id === activeProject.id
                          ? 'bg-violet-50 text-violet-700'
                          : 'hover:bg-slate-50 text-slate-700'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded shrink-0">
                            {project.code || '—'}
                          </span>
                          <span className="text-sm font-medium truncate">
                            {project.name}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {getStatusBadge(project.status)}
                        {project.id === activeProject.id && (
                          <Check className="h-4 w-4 text-violet-600" />
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        )}
      </div>

      {/* Schedule Context (if active) */}
      {activeSchedule && (
        <>
          <Separator orientation="vertical" className="h-4 bg-slate-200" />
          <div 
            className="flex items-center gap-2 text-sm text-slate-500"
            data-testid="active-schedule-indicator"
          >
            <CalendarDays className="h-3.5 w-3.5" />
            <span className="max-w-[150px] truncate text-xs">
              {activeSchedule.name}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default ActiveProjectHeader;
